import { isSlotTaken, useBookPatientVisit } from '@/api/hooks/useBookPatientVisit'
import { useFetchUpcomingVisits } from '@/api/hooks/useFetchUpcomingVisits'
import { usePatientAvailability } from '@/api/hooks/usePatientAvailability'
import type { BookedVisit } from '@/api/types/ProviderApi'
import { ErrorState } from '@/components/ErrorState'
import ProtectedPage from '@/components/ProtectedPage'
import { SchedulePicker } from '@/components/SchedulePicker'
import { BookedNotice } from '@/components/SchedulePicker/BookedNotice'
import { ConfirmSheet } from '@/components/SchedulePicker/ConfirmSheet'
import { LoadingRule } from '@/components/SchedulePicker/LoadingRule'
import { RescheduleContext } from '@/components/SchedulePicker/RescheduleContext'
import { SchedulePickerSkeleton } from '@/components/SchedulePicker/SchedulePickerSkeleton'
import { StudioSheet } from '@/components/SchedulePicker/StudioSheet'
import { CHOICE_HOLD_MS } from '@/constants/timing'
import {
	NO_EXPANSION,
	type StudioSection,
	type TimeChip,
	addWeeks,
	buildWeekView,
	chipStillExists,
	firstOpenDay,
	weekStartOf,
} from '@/features/scheduling/availability'
import { analytics } from '@/lib/analytics'
import { FlowHeader } from '@/ui/core/flow-header'
import { PageShell } from '@/ui/core/page-shell'
import { Screen } from '@/ui/core/screen'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import * as Crypto from 'expo-crypto'
import { router, useLocalSearchParams } from 'expo-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Toast from 'react-native-toast-message'
import Images from '#/assets/images/image-map'

const goHome = () => (router.canGoBack() ? router.back() : router.replace('/'))

/**
 * Picking a time, natively.
 *
 * One screen and one decision: choose a studio's opening, confirm it, done. The confirm step is
 * deliberate rather than booking on the first tap, since a booking is not something the patient
 * can undo from here, and a mis-tap in a row of times is easy.
 */
export default function ScheduleScreen() {
	const { mode, appointmentId } = useLocalSearchParams<{
		mode?: 'book' | 'reschedule'
		appointmentId?: string
	}>()
	const isReschedule = mode === 'reschedule'
	const rescheduleAppointmentId = isReschedule
		? Number.parseInt(appointmentId ?? '', 10)
		: undefined

	const insets = useSafeAreaInsets()
	const [weekStart, setWeekStart] = useState(() => weekStartOf(new Date()))
	// The day being looked at. Held as an override rather than a value: unset, it falls to the
	// week's first opening, so stepping to another week lands on that week's soonest without
	// anything having to remember to reset it.
	const [dayOverride, setDayOverride] = useState<string>()
	const [studioSheet, setStudioSheet] = useState<StudioSection>()
	const [selected, setSelected] = useState<TimeChip>()
	const [isCalendarOpen, setIsCalendarOpen] = useState(false)
	// Undefined means the patient's own geo, which the server picks.
	const [geoId, setGeoId] = useState<number>()
	const [booked, setBooked] = useState<BookedVisit>()
	// Shown inside the confirm sheet. A toast cannot be: a sheet is a modal and draws above the
	// React tree, so the message would come up behind the thing that caused it.
	const [wasTaken, setWasTaken] = useState(false)
	const now = useMemo(() => new Date(), [])

	/**
	 * One key per chosen time, kept across retries of it.
	 *
	 * A retry after a lost response must carry the same key, or the server cannot tell it from a
	 * second booking. It is cleared only when the patient picks a different time.
	 */
	const attemptKey = useRef<string | undefined>(undefined)
	const hasBooked = useRef(false)
	/** Held open for a beat after a date is picked, so the mark is seen to land. */
	const calendarTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
	useEffect(() => () => clearTimeout(calendarTimer.current), [])

	const { availability, isLoading, isChangingWeek, isError, refetch } =
		usePatientAvailability({
			weekStart,
			geoId,
			rescheduleAppointmentId,
		})
	// The week carries its own context, so the picker asks one question rather than two.
	const bookingContext = availability?.bookingContext

	// The server may answer for a later week than we asked for, when everything we asked for
	// falls before the first date this patient can book. Follow it rather than draw an empty week.
	const answeredWeek = availability?.weekStart
	useEffect(() => {
		if (answeredWeek && answeredWeek !== weekStart) {
			setWeekStart(answeredWeek)
		}
	}, [answeredWeek, weekStart])
	const { mutate: book, isPending: isBooking } = useBookPatientVisit()
	// Already cached from the card the patient tapped, so naming the visit costs no round trip.
	const { visits } = useFetchUpcomingVisits()
	const movingVisit = visits?.find(
		(visit) => visit.appointmentId === rescheduleAppointmentId
	)

	const view = useMemo(
		() =>
			buildWeekView({
				availability,
				weekStart,
				now,
				expansion: NO_EXPANSION,
				preferLocationId: movingVisit?.locationId,
			}),
		[availability, weekStart, now, movingVisit?.locationId]
	)

	const chosenDate = dayOverride ?? firstOpenDay(view)

	const studioNameFor = (chip?: TimeChip) =>
		view.studios.find((studio) => studio.locationId === chip?.locationId)?.name ?? ''

	const goToWeek = (next: string) => {
		setWeekStart(next)
		setSelected(undefined)
		setWasTaken(false)
		setDayOverride(undefined)
	}

	const onConfirm = () => {
		if (!selected || hasBooked.current) {
			return
		}
		attemptKey.current ??= Crypto.randomUUID()
		analytics.trackClick('book-visit-confirm', { locationId: selected.locationId })

		book(
			{
				locationId: selected.locationId,
				podId: selected.podId,
				startTimeLocal: selected.startTimeLocal,
				idempotencyKey: attemptKey.current,
				rescheduleAppointmentId,
			},
			{
				onSuccess: (visit) => {
					hasBooked.current = true
					setBooked(visit)
				},
				onError: (error) => {
					if (isSlotTaken(error)) {
						analytics.trackEvent('booking-slot-taken', {
							locationId: selected.locationId,
							date: selected.date,
						})
						// The server let the claim go, so the next time gets its own key.
						attemptKey.current = undefined
						// The week, the open studios and the scroll all stay put; only the time goes.
						setSelected(undefined)
						setWasTaken(true)
						refetch()
						return
					}
					// Our words, not the server's. `error.message` on a 500 is the HTTP status
					// phrase — a member was being shown "Internal Server Error" — and the API
					// makes no promise that anything it returns is fit to read. The one server
					// condition worth naming is the taken slot, and that is handled above.
					Toast.show({
						type: 'error',
						position: 'top',
						topOffset: insets.top + 8,
						text1: isReschedule
							? "We couldn't move your visit. Try again."
							: "We couldn't book that time. Try again.",
					})
				},
			}
		)
	}

	const renderBody = () => {
		if (isLoading) {
			return <SchedulePickerSkeleton />
		}

		if (isError || !availability || !bookingContext) {
			return (
				<ErrorState
					title="We couldn't load times"
					line="Check your connection and try again."
					onAction={() => refetch()}
				/>
			)
		}

		return (
			<VStack gap={24}>
				{/* The promo strip, in coral: something changed under you, and nothing broke.
				    Above the line about the visit being moved, because it is an interruption and
				    that is steady state. */}
				{wasTaken && (
					<View className="rounded-brand bg-accent px-4 py-3">
						<Text weight="bold" tone="white">
							That time was just taken.{' '}
							{isReschedule
								? 'Your existing visit is untouched.'
								: "Here's what's still open."}
						</Text>
					</View>
				)}
				{/* Closer to the picker than the page's own rhythm allows, because it belongs to
				    it: the line says what this week of times is being chosen for, and at
				    twenty-four it read as a separate block sitting above one. Nested rather than
				    given a negative margin, so the two are one thing with its own spacing. */}
				<VStack gap={14}>
					{movingVisit && <RescheduleContext visit={movingVisit} now={now} />}
					<SchedulePicker
						view={view}
						bounds={{
							earliestBookableDate: bookingContext.earliestBookableDate,
							maxBookableDate: bookingContext.maxBookableDate,
						}}
						selectedKey={selected?.key}
						isCalendarOpen={isCalendarOpen}
						chosenDate={chosenDate}
						geos={availability.geos}
						geoId={availability.geoId}
						onToggleCalendar={() => {
							clearTimeout(calendarTimer.current)
							setIsCalendarOpen((open) => !open)
						}}
						onChooseDay={setDayOverride}
						onChooseGeo={(next) => {
							setSelected(undefined)
							setWasTaken(false)
							setDayOverride(undefined)
							setGeoId(next)
						}}
						onOpenStudio={setStudioSheet}
						onWeekStep={(step) => goToWeek(addWeeks(weekStart, step))}
						onPickDate={(date) => {
							// The week the day falls in, and then the day itself. Going through
							// goToWeek alone threw the tap away: it clears the override, so picking
							// the fifteenth landed on whichever day that week happened to open first.
							goToWeek(weekStartOf(date))
							setDayOverride(date)
							// The sheet stays a beat so the chosen day is seen to take the mark and
							// its week to go grey around it. Closing on the touch meant the calendar
							// was gone before it could acknowledge anything, and the only evidence a
							// tap had landed was a heading changing behind it. Same count as the city
							// sheet, so an answer is acknowledged at one speed.
							clearTimeout(calendarTimer.current)
							calendarTimer.current = setTimeout(
								() => setIsCalendarOpen(false),
								CHOICE_HOLD_MS
							)
						}}
						onSelect={(chip) => {
							setWasTaken(false)
							if (chip.key !== selected?.key) {
								attemptKey.current = undefined
							}
							setSelected(chip)
						}}
					/>
				</VStack>
			</VStack>
		)
	}

	const isSelectionStillOffered = selected && chipStillExists(view, selected.key)

	/*
	 * A booking takes the whole screen.
	 *
	 * It is a photograph edge to edge with its own chrome over it, so it cannot sit inside the
	 * page shell the picker uses — the shell gives its children the page's margins and its pink,
	 * which is everything this screen is not. No safe edges either: the notice takes the insets
	 * itself, so the picture runs under the clock and the indicator while the chrome does not.
	 */
	if (booked) {
		return (
			<ProtectedPage>
				<Screen preset="fixed" className="flex flex-1">
					<BookedNotice
						visit={booked}
						isReschedule={isReschedule}
						onSeeVisit={() =>
							router.replace({
								pathname: '/visits/[id]',
								params: { id: String(booked.appointmentId) },
							})
						}
						onDone={goHome}
					/>
				</Screen>
			</ProtectedPage>
		)
	}

	return (
		<ProtectedPage>
			<Screen preset="fixed" className="flex flex-1" safeAreaEdges={['top', 'bottom']}>
				{/* The header draws the bare cross and the ink rule itself now. `LoadingRule`
				    below is this screen's own floor and travels while a week loads, so the
				    header's rule is off — two would be one too many. */}
				<FlowHeader rule={false} onClose={goHome} />
				{/* A rule under the chrome, edge to edge, which doubles as the week's loader.
				
				    It runs past the page's own margins on purpose: a line that stopped where the
				    content starts would be a third margin on a screen that already has one for
				    type and one for panels, and would read as an underline belonging to the
				    heading below it rather than as a floor under the wordmark above. Black, since
				    the only other rules here are the pink fold and the row divider, and neither is
				    strong enough to hold a header off a scrolling page. */}
				<LoadingRule active={isChangingWeek} />
				<View className="flex-1">
					<PageShell photo={Images.BrandPhoto}>{renderBody()}</PageShell>
				</View>
				<ConfirmSheet
					chip={!booked && selected && isSelectionStillOffered ? selected : undefined}
					studioName={studioNameFor(selected)}
					geoName={availability?.geos.find((geo) => geo.id === availability.geoId)?.name}
					now={now}
					cta={isReschedule ? 'Move to This Time' : 'Book This Time'}
					note={
						isReschedule && movingVisit
							? 'Your other appointment will be released.'
							: undefined
					}
					isBooking={isBooking}
					onConfirm={onConfirm}
					onCancel={() => setSelected(undefined)}
				/>
				<StudioSheet studio={studioSheet} onClose={() => setStudioSheet(undefined)} />
			</Screen>
		</ProtectedPage>
	)
}
